/**
 * Stil per objekt for lag fra klassifiseringsmotoren (classify). Rene funksjoner; tegningen skjer i overlays.ts.
 *
 * 1. Bæreobjekter, isolatorer og ledere: er objektet skrevet til punktskyen?
 *    `written_to_pointcloud` / `written_flag` (0/1) brukes når feltet finnes: 1 → heltrukket, 0 → stiplet/dempet.
 *    Ellers `status`: VALIDATED → heltrukket, UNCERTAIN → stiplet/dempet. REJECTED skjules (unntatt debug-lag).
 *    Barduner, traverser, stolpeakser og isolatorer tegnes i fargen til punktklassen de er skrevet som
 *    (64 Bardun, 65 Travers, 15 Stolpe, 16 Isolator), så lag og punktsky har samme farge. Ledere: spenningsnivå.
 * 2. Fallrisiko fra toppen (fallrisk-pakken / nye vegetasjonslag): toppens bue fra tretoppen (prikk) ned til
 *    kontaktøyeblikket – rød når treet treffer ledningen (`hits_geometric` = 1, H ≥ d3), oransje når det bare kommer
 *    innenfor minsteavstanden (`hits` = 1). Mørkerød, tykk strek = delen av treet over linja (`veg_fall_overshoot`).
 *    Ring = kontaktpunktet på linja med etikett «3,6 m over» / «1,2 m kort». Strekbredden øker med `load_index`
 *    (relativ, ikke kraft). Gamle fall-linjer (basis → treffpunkt) har samme fargeregel, men er skjult som standard.
 * 2b. Resultat-visning: bæreobjekt-kandidater som ikke er skrevet til punktskyen (usikre stolper, traverser,
 *    barduner) skjules når `viewOptions.hideUnwrittenSupports` er satt (bryter i Funn-panelet).
 * 3. Minsteavstand: avstandslinje vegetasjon–ledning, rød når `deficit_m` > 0, ellers gul, med etikett.
 * 4. Bakkeavstand (pakke «bakkeavstand»): loddrett linje fra lederens laveste punkt ned til bakken (`bakkeavstand`) og
 *    ledning → topp av objekt under (`objektavstand`). Bare styrende leder per spenn (`styrende` = 1) som standard.
 *    Farge etter `brudd`: 1 rød, 0 grønn, −1 (ikke kontrollert) nøytral. Objekter: farge etter kategori, «annet» dempet.
 * 5. Kundevisning (leveranse.ts, Resultat + «Som kunden ser det»): bare det leveranseprofilen tar med tegnes – fallrisiko
 *    etter profilens modus, bare brudd på minsteavstand, bakkeavstand grå (til orientering) til et krav er satt,
 *    objektavstand bare ved brudd. Skjeve master (helning over profilens grense) får rød prikk og «Masten heller 12°».
 * Andre lag tegnes som før (null).
 */

import { classInfo } from '../scene/classes';
import { FALL_LAYER, fallVisible, normalizeFallProps } from './fallRisk';
import { isGoverningRow } from './groundSelection';
import { clearanceViolation, distanceViolation, fallIncluded, isTiltFinding, leveranseView, poleTilt, tiltLabel } from './leveranse';

export interface FeatureStyle {
  hidden?: boolean;
  dashed?: boolean;
  /** dempet farge (blandet mot grått) */
  faded?: boolean;
  /** overstyrer lag-/statusfargen */
  color?: string;
  /** punktstørrelse (px) */
  size?: number;
  /** linjebredde (px) */
  width?: number;
  /** skjermtekst ved linjens midtpunkt */
  label?: string;
  /** rekkefølge for etiketter: høyest først når de kolliderer */
  priority?: number;
  /** markør i linjens første punkt (f.eks. tretoppen) */
  startMarker?: { color: string; size: number };
  /** punktmarkør som ring (hul) i stedet for fylt prikk */
  ring?: boolean;
}

/** Visningsvalg som gjelder alle lag (settes av UI-et). */
export const viewOptions = { hideUnwrittenSupports: false, showAllGroundWires: false };
export function setHideUnwrittenSupports(v: boolean): void { viewOptions.hideUnwrittenSupports = v; }
/** Bakkeavstand: vis alle ledere i spennet, ikke bare den styrende (laveste) */
export function setShowAllGroundWires(v: boolean): void { viewOptions.showAllGroundWires = v; }
export const GROUND_LAYER = 'bakkeavstand', OBJECT_CLEARANCE_LAYER = 'objektavstand';
export const GROUND_LAYERS = [GROUND_LAYER, OBJECT_CLEARANCE_LAYER] as const;
export const ALL_GROUND_WIRES_TOGGLE = 'Vis alle målinger (ikke bare den laveste per spenn)';
export const GROUND_NOTE = 'Målt nedheng ved flyging – ikke maks nedheng ved høy temperatur eller islast. UNCALIBRATED – ikke forskriftsmessig.';
export const GREEN = '#22c55e', NEUTRAL = '#cbd5e1';
/** Objektkategori under ledningen → farge */
export const OBJECT_CATEGORY_COLORS: Readonly<Record<string, string>> = { bygg: '#f59e0b', vegetasjon: '#4ade80', annet: '#94a3b8' };
/** Bæreobjektlag der kandidater som ikke er skrevet til punktskyen kan skjules i Resultat-visningen. */
export const SUPPORT_CANDIDATE_LAYERS = ['support_poles', 'support_pole_axes', 'support_traverses', 'support_guys'] as const;
export const UNCERTAIN_SUPPORTS_TOGGLE = 'Vis usikre mastkandidater (ikke klassifisert)';

/** Lagene fra classify-GeoPackagene (supports / insulators / conductors) der heltrukket/stiplet gjelder. */
export const WRITE_STYLE_LAYERS = new Set([
  'support_poles', 'support_pole_axes', 'support_traverses', 'support_guys', 'insulators', 'conductor_wires', 'conductor_spans',
]);
/** Lag → punktklassen objektene skrives som. Laget tegnes i klassens farge (samme som i punktskyen og legenden). */
export const LAYER_CLASS: Readonly<Record<string, number>> = { support_pole_axes: 15, support_guys: 64, support_traverses: 65, insulators: 16 };
export const layerClassColor = (layerId: string): string | undefined =>
  LAYER_CLASS[layerId] !== undefined ? classInfo(LAYER_CLASS[layerId]).color : undefined;
export const WRITE_STYLE_NOTE = 'Heltrukket = klassifisert i punktskyen, stiplet = usikker / ikke klassifisert.';

/** Fallrisiko fra toppen (nye lag) – foretrekkes framfor de gamle basis → treffpunkt-linjene. */
export const FALL_RISK_LAYERS = ['veg_fall_top_arcs', 'veg_fall_overshoot', 'veg_fall_contacts'] as const;
export const FALL_LAYERS = ['veg_fall_lines', 'veg_fall_impacts', 'veg_fall_arcs', ...FALL_RISK_LAYERS, FALL_LAYER] as const;
export const CLEARANCE_LAYERS = ['veg_clearance_lines'] as const;
/** Én bryter: nytt enkeltlag `fallrisiko` når det finnes, ellers dagens tre lag (bue + del over linja + kontaktpunkt). */
export const FALL_TOGGLE = { title: 'Fallrisiko (trær mot linja)', layers: [FALL_LAYER] as string[], fallback: [...FALL_RISK_LAYERS] as string[] };
/**
 * Eldre fallrisiko-/faretre-lag. Fallrisiko vises som ett logisk lag (FALL_RISK_LAYERS: bue + del over linja + kontaktpunkt);
 * disse er fjernet fra laglistene og finnes bare i eksportens «Avansert – enkeltlag».
 */
export const LEGACY_FALL_LAYERS: ReadonlySet<string> = new Set(['veg_fall_lines', 'veg_fall_impacts', 'veg_fall_arcs', 'veg_danger_trees']);
/** Lag som ikke får egen rad i laglistene: eldre fall-lag og delene av den sammenslåtte fallrisikoen (vises som én bryter). */
export const hiddenFromLayerList = (id: string): boolean => LEGACY_FALL_LAYERS.has(id) || id === FALL_LAYER || (FALL_RISK_LAYERS as readonly string[]).includes(id);
export const CLEARANCE_TOGGLE = { title: 'Minsteavstand (vegetasjon–linje)', layers: ['veg_clearance_lines'] };
export const FALL_LEGEND = 'Bue fra tretoppen (prikk) ned til linja: rød = treet treffer linja, oransje = kommer innenfor minsteavstanden. '
  + 'Mørkerød tykk strek = delen av treet over linja. Ring = kontaktpunkt med «m over» / «m kort». Tykkere strek = høyere last-indeks (relativ, ikke kraft).';
export const CLEARANCE_LEGEND = 'Linje fra nærmeste vegetasjonspunkt til nærmeste ledningspunkt: rød = for kort avstand, gul = innenfor krav. Etiketten viser avstand og krav.';
export const VEG_NOTE = 'UNCALIBRATED – ikke forskriftsmessig. Geometrisk modell fra punktskyen; avstander og marginer er ikke kontrollert mot forskriftskrav.';

export const RED = '#ef4444', ORANGE = '#f97316', YELLOW = '#facc15', LIGHT_RED = '#fca5a5', DARK_RED = '#991b1b';

export const isVegRiskLayer = (id: string) => (FALL_LAYERS as readonly string[]).includes(id) || (CLEARANCE_LAYERS as readonly string[]).includes(id);
const isDebugLayer = (id: string) => /debug|rejected|forkast/i.test(id);

/** 0/1, true/false, "0"/"1" → boolsk; ellers undefined. */
export function flag(v: unknown): boolean | undefined {
  if (v === true || v === 1 || v === '1') return true;
  if (v === false || v === 0 || v === '0') return false;
  return undefined;
}

const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : undefined;
};
const m1 = (v: number) => v.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Etikett for en avstandslinje: `label` fra analysen, ellers «2,1 m (krav 3,0 m)». */
export function clearanceLabel(props: Record<string, unknown>): string | undefined {
  if (typeof props.label === 'string' && props.label.trim()) return props.label.trim();
  const d = num(props.dist_3d_m);
  if (d === undefined) return undefined;
  const req = num(props.required_clearance_m);
  return `${m1(d)} m` + (req !== undefined ? ` (krav ${m1(req)} m)` : '');
}

/**
 * Er objektet skrevet til punktskyen? `written_to_pointcloud` (bæreobjekter/ledere), `written_flag` (isolatorer),
 * ellers `written` (antall skrevne punkter, > 0 = ja). undefined = ukjent.
 */
export function writtenFlag(props: Record<string, unknown>): boolean | undefined {
  const w = flag(props.written_to_pointcloud) ?? flag(props.written_flag);
  if (w !== undefined) return w;
  const n = num(props.written);
  return n !== undefined && typeof props.written !== 'boolean' ? n > 0 : flag(props.written);
}

/**
 * Usikker bæreobjekt-kandidat: ikke skrevet til punktskyen (`written_to_pointcloud` = 0), ellers status ≠ VALIDATED.
 * Forkastede regnes ikke (de skjules alltid). Ukjent (verken flagg eller status) = false.
 */
export function isUnwrittenSupport(layerId: string, props: Record<string, unknown>): boolean {
  if (!(SUPPORT_CANDIDATE_LAYERS as readonly string[]).includes(layerId)) return false;
  const status = typeof props.status === 'string' ? props.status.trim().toUpperCase() : undefined;
  if (status === 'REJECTED') return false;
  const w = writtenFlag(props);
  if (w !== undefined) return !w;
  return status !== undefined && status !== 'VALIDATED';
}

/** Antall bæreobjekter skrevet til punktskyen og usikre kandidater (unike ID-er; forkastede telles ikke). */
export function supportCounts(layerId: string, features: { id?: unknown; props: Record<string, unknown> }[]): { written: number; uncertain: number } {
  const w = new Set<string>(), u = new Set<string>();
  features.forEach((f, i) => {
    const st = typeof f.props.status === 'string' ? f.props.status.trim().toUpperCase() : '';
    if (st === 'REJECTED') return;
    const key = f.id !== undefined && f.id !== null && f.id !== '' ? String(f.id) : `#${i}`;
    (isUnwrittenSupport(layerId, f.props) ? u : w).add(key);
  });
  return { written: w.size, uncertain: u.size };
}

export function writeStyle(layerId: string, props: Record<string, unknown>): FeatureStyle | null {
  if (!WRITE_STYLE_LAYERS.has(layerId)) return null;
  const status = typeof props.status === 'string' ? props.status.trim().toUpperCase() : undefined;
  if (status === 'REJECTED' && !isDebugLayer(layerId)) return { hidden: true };
  if (viewOptions.hideUnwrittenSupports && isUnwrittenSupport(layerId, props)) return { hidden: true };
  const w = writtenFlag(props);
  if (w !== undefined) return w ? {} : { dashed: true, faded: true };
  if (status === 'VALIDATED') return {};
  if (status === 'UNCERTAIN') return { dashed: true, faded: true };
  return null;
}

export function featureStyle(layerId: string, props: Record<string, unknown>): FeatureStyle | null {
  if (leveranseView.customer && customerHidden(layerId, props)) return { hidden: true };
  return baseStyle(layerId, props);
}

/** Kundevisning: objekter leveranseprofilen ikke tar med (se leveranse.ts). */
export function customerHidden(layerId: string, props: Record<string, unknown>): boolean {
  const profil = leveranseView.profil;
  switch (layerId) {
    case FALL_LAYER: case 'veg_fall_top_arcs': case 'veg_fall_overshoot': case 'veg_fall_contacts':
      return !fallIncluded(normalizeFallProps(props), profil);
    case 'veg_fall_lines': case 'veg_fall_impacts': case 'veg_fall_arcs': case 'veg_danger_trees':
      return true; // eldre fall-lag: ikke en del av leveransen
    case 'veg_clearance_lines':
      return !profil.minsteavstand.vis || !clearanceViolation(props);
    case GROUND_LAYER:
      return !profil.bakkeavstand.vis || !isGoverningRow(props);
    case OBJECT_CLEARANCE_LAYER:
      return !profil.objektavstand.vis || !isGoverningRow(props) || !distanceViolation(props, 'avstand_m', profil.objektavstand.krav_m);
    case 'noise_clusters': case 'noise_cluster_points':
      return true;
    default:
      return isUnwrittenSupport(layerId, props) || (layerId === 'insulators' && writtenFlag(props) === false);
  }
}

function baseStyle(layerId: string, props: Record<string, unknown>): FeatureStyle | null {
  switch (layerId) {
    // hits_geometric = H ≥ d3 (treffer ledningen); hits = H ≥ d3 − terskel (innenfor minsteavstanden)
    case 'veg_fall_lines': {
      if (flag(props.hits_geometric)) return { color: RED, width: 3 };
      if (flag(props.hits)) return { color: ORANGE, width: 2.5 };
      return { hidden: true };
    }
    case 'veg_fall_impacts':
      return { color: flag(props.hits_geometric) === false ? ORANGE : RED, size: 15 };
    case 'veg_fall_arcs': {
      if (!flag(props.hits) && !flag(props.hits_geometric)) return { hidden: true };
      return { color: flag(props.hits_geometric) ? LIGHT_RED : ORANGE, dashed: true, width: 1.5 };
    }
    case 'veg_fall_top_arcs': {
      if (!fallVisible(props)) return { hidden: true };
      const geo = !!flag(props.hits_geometric);
      const color = geo ? RED : ORANGE;
      return { color, width: geo ? loadWidth(props, 2, 3) : 2, startMarker: { color, size: 10 } };
    }
    case 'veg_fall_overshoot':
      if (!flag(props.hits_geometric) || !fallVisible(props)) return { hidden: true };
      return { color: DARK_RED, width: loadWidth(props, 5, 3) };
    case FALL_LAYER: {
      // nytt enkeltlag: én linje per tre (bue → kontakt → del over linja), feltene oversettes i fallRisk.ts
      const p = normalizeFallProps(props);
      if (!fallVisible(p)) return { hidden: true };
      const geo = !!flag(p.hits_geometric);
      const color = geo ? RED : ORANGE;
      return { color, width: geo ? loadWidth(p, 2.5, 3) : 2, startMarker: { color, size: 10 }, label: fallContactLabel(p), priority: fallPriority(p) };
    }
    case 'veg_fall_contacts': {
      if (!fallVisible(props)) return { hidden: true };
      const geo = !!flag(props.hits_geometric);
      return { color: geo ? RED : ORANGE, size: 18, ring: true, label: fallContactLabel(props), priority: fallPriority(props) };
    }
    case 'veg_clearance_lines': {
      const deficit = num(props.deficit_m);
      const short = deficit !== undefined && deficit > 0;
      return { color: short ? RED : YELLOW, width: 3, label: clearanceLabel(props), priority: deficit ?? -Infinity };
    }
    case GROUND_LAYER: {
      // én måling per mast–mast-spenn (groundSelection.ts); «Vis alle målinger» i Full visning viser resten
      if (!viewOptions.showAllGroundWires && !isGoverningRow(props)) return { hidden: true };
      const b = groundState(props, 'min_hoyde_m', leveranseView.profil.bakkeavstand.krav_m);
      const color = b === 1 ? RED : b === 0 ? GREEN : NEUTRAL;
      const h = num(props.min_hoyde_m);
      return { color, width: b === 1 ? 3.5 : 2.5, label: groundLabel(props), priority: b === 1 ? 1000 - (h ?? 0) : -(h ?? 1000),
        startMarker: { color, size: 7 } };
    }
    case OBJECT_CLEARANCE_LAYER: {
      if (!viewOptions.showAllGroundWires && !isGoverningRow(props)) return { hidden: true };
      const b = groundState(props, 'avstand_m', leveranseView.profil.objektavstand.krav_m);
      const cat = typeof props.kategori === 'string' ? props.kategori.trim().toLowerCase() : 'annet';
      const color = b === 1 ? RED : OBJECT_CATEGORY_COLORS[cat] ?? OBJECT_CATEGORY_COLORS.annet;
      const d = num(props.avstand_m);
      return { color, width: b === 1 ? 3 : 2, dashed: cat === 'annet', faded: cat === 'annet' && b !== 1, label: groundLabel(props),
        priority: b === 1 ? 900 - (d ?? 0) : cat === 'annet' ? -2000 - (d ?? 0) : -(d ?? 1000) };
    }
    case 'conductor_wires':
    case 'conductor_spans': {
      // farge etter spenningsnivå (0 ukjent, 1 lavspent, 2 høyspent distribusjon, 3 regional/transmisjon) + strek etter status
      const w = writeStyle(layerId, props);
      if (w?.hidden) return w;
      const v = num(props.voltage_level);
      const color = v !== undefined && Number.isInteger(v) && v >= 0 && v < VOLTAGE_COLORS.length ? VOLTAGE_COLORS[v] : undefined;
      return color ? { ...(w ?? {}), color } : w;
    }
    case 'support_poles': {
      // skjev mast (helning over leveranseprofilens grense): rød prikk med «Masten heller 12°»
      const w = writeStyle(layerId, props);
      if (w?.hidden || !isTiltFinding(props, leveranseView.profil)) return w;
      const t = poleTilt(props)!;
      return { ...(w ?? {}), color: RED, size: 14, label: tiltLabel(t), priority: 500 + t };
    }
    case 'insulators': {
      // korte streker (feste → ledning, under 1 m): bredere så de synes ved masten
      const w = writeStyle(layerId, props);
      return w?.hidden ? w : { ...(w ?? {}), color: layerClassColor(layerId), width: 4 };
    }
    default: {
      const w = writeStyle(layerId, props);
      const color = layerClassColor(layerId);
      return color && !w?.hidden ? { ...(w ?? {}), color } : w;
    }
  }
}

/** Brudd-tilstand for bakke-/objektavstand: 1 brudd, 0 innenfor krav, −1 ingen krav (profilens krav går foran motorens). */
export function groundState(props: Record<string, unknown>, key: 'min_hoyde_m' | 'avstand_m', profilKrav: number | null): 1 | 0 | -1 {
  if (profilKrav === null) { const b = num(props.brudd); return b === 1 ? 1 : b === 0 ? 0 : -1; }
  return distanceViolation(props, key, profilKrav) ? 1 : 0;
}

/** Etikett for bakkeavstand/objektavstand: `etikett` fra motoren, ellers «7,2 m». */
export function groundLabel(props: Record<string, unknown>): string | undefined {
  if (typeof props.etikett === 'string' && props.etikett.trim()) return props.etikett.trim();
  const v = num(props.min_hoyde_m) ?? num(props.avstand_m);
  return v === undefined ? undefined : `${m1(v)} m`;
}

/** Oppsummering for Funn-raden: spenn (én måling per mast–mast-spenn), laveste høyde og antall brudd. */
export function groundSummary(features: { props: Record<string, unknown> }[], key: 'min_hoyde_m' | 'avstand_m' = 'min_hoyde_m'): { spans: number; lowest?: number; breaches: number; items: number } {
  const gov = features.filter((f) => isGoverningRow(f.props));
  const spans = new Set(gov.map((f, i) => String(f.props.segment_id ?? `#${i}`)));
  const vals = gov.map((f) => num(f.props[key])).filter((v): v is number => v !== undefined);
  return { spans: spans.size, lowest: vals.length ? Math.min(...vals) : undefined, breaches: gov.filter((f) => num(f.props.brudd) === 1).length, items: gov.length };
}

/** Strekbredde: `base` + `extra`·last-indeks (0–1, relativ; mangler → `base`). */
export function loadWidth(props: Record<string, unknown>, base: number, extra: number): number {
  const li = num(props.load_index);
  return li === undefined ? base : Math.round((base + extra * Math.max(0, Math.min(1, li))) * 10) / 10;
}

/** Kort etikett ved kontaktpunktet: «3,6 m over» (treffer) / «1,2 m kort» (innenfor minsteavstanden). */
export function fallContactLabel(props: Record<string, unknown>): string | undefined {
  if (typeof props.label_short === 'string' && props.label_short.trim()) return props.label_short.trim();
  const over = num(props.overshoot_m), short = num(props.shortfall_m);
  if (flag(props.hits_geometric) && over !== undefined) return `${m1(over)} m over`;
  if (short !== undefined) return `${m1(short)} m kort`;
  return undefined;
}

/** Prioritet (etiketter og funnrekkefølge): treff etter lengde over linja (størst først), deretter nesten-treff. */
export function fallPriority(props: Record<string, unknown>): number {
  if (flag(props.hits_geometric)) return 1000 + (num(props.overshoot_m) ?? 0);
  return -(num(props.shortfall_m) ?? 1000);
}

/** Spenningsnivå → farge (samme rekkefølge som VOLTAGE_LEVELS i fields.ts). */
export const VOLTAGE_COLORS = ['#94a3b8', '#22d3ee', '#facc15', '#f472b6'];

export interface LabelCandidate { x: number; y: number; text: string; priority: number }

/**
 * Velg etiketter som ikke overlapper på skjermen: høyest prioritet først (størst underskudd), deretter de som
 * får plass. Etikettboksen anslås fra tekstlengden. Utenfor skjermen hoppes over.
 */
export function declutterLabels(c: LabelCandidate[], width: number, height: number, max = 60, charW = 7, boxH = 18): LabelCandidate[] {
  const placed: { l: LabelCandidate; x0: number; x1: number; y0: number; y1: number }[] = [];
  const sorted = [...c].sort((a, b) => b.priority - a.priority);
  for (const l of sorted) {
    if (placed.length >= max) break;
    if (l.x < 0 || l.y < 0 || l.x > width || l.y > height) continue;
    const w = l.text.length * charW + 10;
    const box = { x0: l.x - w / 2, x1: l.x + w / 2, y0: l.y - boxH / 2, y1: l.y + boxH / 2 };
    if (placed.some((p) => box.x0 < p.x1 + 4 && box.x1 > p.x0 - 4 && box.y0 < p.y1 + 2 && box.y1 > p.y0 - 2)) continue;
    placed.push({ l, ...box });
  }
  return placed.map((p) => p.l);
}
